import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Paper, Rating, Stack, TextField, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShieldIcon from '@mui/icons-material/Shield';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../utils/apiFetch.js';

const money = (value) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value || 0);

export default function JobCompletionApproval({ paymentId, onCompletion }) {
  const { paymentId: routePaymentId } = useParams();
  const activePaymentId = paymentId || routePaymentId;
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [satisfactionRating, setSatisfactionRating] = useState(5);
  const [completionNotes, setCompletionNotes] = useState('');
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueDescription, setIssueDescription] = useState('');

  const fetchPaymentStatus = async () => {
    try {
      const response = await apiFetch(`/api/escrow/payment-status/${activePaymentId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || `HTTP ${response.status}`);
      setPaymentStatus(data);
    } catch {
      setError('We could not load this move confirmation. Please contact MOVD support.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activePaymentId) fetchPaymentStatus();
  }, [activePaymentId]);

  const approveCompletion = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await apiFetch('/api/escrow/complete-job', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentId: activePaymentId,
          satisfactionRating,
          completionNotes,
          photoUrls: [],
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || `HTTP ${response.status}`);
      setSuccessMessage('Thanks. Your move has been approved and escrow release has started.');
      onCompletion?.(data);
      await fetchPaymentStatus();
    } catch (err) {
      setError(err?.message || 'Unable to approve completion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reportIssue = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await apiFetch('/api/escrow/report-issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentId: activePaymentId,
          issueDescription,
          satisfactionRating,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || `HTTP ${response.status}`);
      setIssueOpen(false);
      setSuccessMessage('Your issue has been reported. MOVD will review it before funds are released.');
      await fetchPaymentStatus();
    } catch (err) {
      setError(err?.message || 'Unable to report issue. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  }

  if (!paymentStatus?.payment) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Payment information not found.</Alert></Box>;
  }

  const { payment, daysUntilAutoRelease } = paymentStatus;
  const booking = payment.booking || {};

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F5F7FA', p: { xs: 2, md: 5 } }}>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, color: '#fff', background: 'linear-gradient(120deg, #102A43 0%, #176B87 100%)', mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <ShieldIcon sx={{ fontSize: 42, color: '#9FE3D0' }} />
            <Box>
              <Typography variant="overline" sx={{ color: '#9FE3D0', fontWeight: 800, letterSpacing: 1.5 }}>ESCROW REVIEW</Typography>
              <Typography variant="h4" fontWeight={900}>Confirm Your Move</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.76)', mt: 0.5 }}>Tell us whether the job was completed properly before funds are released.</Typography>
            </Box>
          </Stack>
        </Paper>

        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper elevation={0} sx={{ p: 3, border: '1px solid #D9E2EC', borderRadius: 3, mb: 3 }}>
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={800}>{payment.mover?.name || 'Moving Company'}</Typography>
              <Chip label={payment.escrowStatus === 'held' ? 'Funds held in escrow' : payment.escrowStatus} color={payment.escrowStatus === 'held' ? 'warning' : 'success'} />
            </Stack>
            <Typography color="text.secondary">Booking {booking.jobNumber || booking._id}</Typography>
            <Typography color="text.secondary">Total paid: <strong>{money(payment.totalAmount)}</strong></Typography>
            {daysUntilAutoRelease > 0 && <Alert severity="info">Funds auto-release in {daysUntilAutoRelease} day{daysUntilAutoRelease === 1 ? '' : 's'} if no issue is reported.</Alert>}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 3, border: '1px solid #D9E2EC', borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>How did the move go?</Typography>
          <Rating value={satisfactionRating} onChange={(event, value) => setSatisfactionRating(value || 1)} size="large" sx={{ mb: 2 }} />
          <TextField fullWidth multiline rows={3} label="Notes (optional)" value={completionNotes} onChange={event => setCompletionNotes(event.target.value)} sx={{ mb: 2 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button variant="contained" size="large" startIcon={<CheckCircleIcon />} disabled={submitting} onClick={approveCompletion}>Approve Completion</Button>
            <Button variant="outlined" color="warning" size="large" startIcon={<ReportProblemIcon />} disabled={submitting} onClick={() => setIssueOpen(true)}>Report an Issue</Button>
          </Stack>
        </Paper>
      </Box>

      <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report an Issue</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>MOVD will hold escrow while our team reviews your concern.</Typography>
          <TextField fullWidth multiline rows={4} label="What happened?" value={issueDescription} onChange={event => setIssueDescription(event.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIssueOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={submitting || !issueDescription.trim()} onClick={reportIssue}>Submit Issue</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
